/**
 * Generated Field Support for Forms (PRD.158 / PRD.159).
 *
 * Provides runtime generation of field values based on configurable patterns.
 * Patient number auto-increment is the first supported use case.
 *
 * ## Phase 1 Sequence Semantics (auto_increment)
 *
 * The `auto_increment` strategy resolves the next integer by:
 *   1. Querying all existing `patient_number` values in the local SQLite DB
 *   2. Filtering to rows that are purely numeric (no letters, no prefixes)
 *   3. Casting those to integers and taking the MAX
 *   4. Returning MAX + 1
 *
 * This means:
 *   - Only purely numeric patient_number values participate in the sequence
 *   - Prefixed values like "P-00042" do NOT affect the counter
 *   - The sequence is local to the device's synced data at generation time
 *   - Two offline devices can generate the same number independently
 *
 * Duplicate handling:
 *   - Local: submit-time validation checks for duplicates before persistence
 *   - Server: syncWrite `beforeWrite` hook appends `-FIXME-{id}` to the
 *     patient_number if a different patient already has it in Postgres.
 *     This is temporary conflict containment, not a final identity strategy.
 *
 * ## Pattern Placeholders
 *
 * | Placeholder       | Resolves to                    |
 * |-------------------|--------------------------------|
 * | {auto_increment}  | Next integer from local data   |
 * | {year}            | Current 4-digit year           |
 * | {month}           | Current 2-digit month (01-12)  |
 * | {day}             | Current 2-digit day (01-31)    |
 */

import type { FormFieldDef, FormFieldsSchemaV1, GeneratedFieldConfig } from './forms';
import { flattenFields } from './forms';

// ─── Types ───

/**
 * DB adapter interface — the caller provides query access.
 * Works with both PowerSync db and any SQL-compatible interface.
 */
export interface GenerationDbAdapter {
  getAll: <T>(sql: string, params?: unknown[]) => Promise<T[]>;
}

// ─── Core Generation ───

/**
 * Phase 1 auto_increment resolution.
 *
 * Scans patient_number values that are purely numeric (no letters, dashes,
 * or other non-digit characters), finds the maximum, and returns max + 1.
 *
 * Important: this only counts rows where patient_number is entirely digits.
 * Prefixed patterns like "P-00042" or "2026-0042" do NOT affect the counter.
 * A future phase could parse existing values against the configured pattern
 * to extract the numeric portion, but Phase 1 keeps it simple and matches
 * the behavior of the original CreatePatientPage.
 */
async function resolveAutoIncrement(db: GenerationDbAdapter): Promise<number> {
  const rows = await db.getAll<{ max_num: number | null }>(
    `SELECT MAX(CAST(patient_number AS INTEGER)) AS max_num ` +
      `FROM patients ` +
      `WHERE patient_number != '' AND patient_number NOT GLOB '*[^0-9]*'`,
  );
  return (rows[0]?.max_num ?? 0) + 1;
}

/**
 * Apply a generation pattern, replacing placeholders with resolved values.
 */
function applyPattern(pattern: string, increment: number, padWidth?: number): string {
  const now = new Date();
  const padded = padWidth ? String(increment).padStart(padWidth, '0') : String(increment);

  return pattern
    .replace(/\{auto_increment\}/g, padded)
    .replace(/\{year\}/g, String(now.getFullYear()))
    .replace(/\{month\}/g, String(now.getMonth() + 1).padStart(2, '0'))
    .replace(/\{day\}/g, String(now.getDate()).padStart(2, '0'));
}

/**
 * Generate a value for a single field based on its generated config.
 */
export async function generateFieldValue(
  db: GenerationDbAdapter,
  config: GeneratedFieldConfig,
): Promise<string> {
  if (config.strategy === 'auto_increment') {
    const next = await resolveAutoIncrement(db);
    return applyPattern(config.pattern, next, config.padWidth);
  }
  return '';
}

/**
 * Scan a schema for fields with generated config and produce initial values.
 * Only generates for fields that don't already have a value.
 *
 * Returns a map of fieldKey → generated value.
 */
export async function generateInitialValues(
  db: GenerationDbAdapter,
  schema: FormFieldsSchemaV1,
  existingValues: Record<string, string>,
): Promise<Record<string, string>> {
  const generated: Record<string, string> = {};
  const allFields = flattenFields(schema);

  for (const field of allFields) {
    if (!field.generated) continue;
    // Don't overwrite user-entered values
    if (existingValues[field.key]?.trim()) continue;

    const value = await generateFieldValue(db, field.generated);
    if (value) {
      generated[field.key] = value;
    }
  }

  return generated;
}

/**
 * Find all generated fields in a schema.
 */
export function getGeneratedFields(schema: FormFieldsSchemaV1): FormFieldDef[] {
  return flattenFields(schema).filter((f) => !!f.generated);
}
